// AI Takeoff Assistant side panel — findings grouped by discipline with
// locate / evidence / measure-on-plan / accept-reject workflow.
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles, X, ChevronDown, ChevronRight, Crosshair, HelpCircle, Ruler,
  AlertTriangle, RotateCcw, Trash2, Eye, CheckCheck,
} from "lucide-react";

export const DISCIPLINE_COLORS = {
  Stormwater: "#38bdf8", Wastewater: "#10b981", Watermain: "#6366f1",
  Earthworks: "#f59e0b", Pavement: "#a78bfa", "Kerb & Channel": "#ec4899",
  Structures: "#94a3b8", Landscaping: "#84cc16", Drainage: "#38bdf8",
  Concrete: "#94a3b8", Other: "#71717a",
};

const CONF_STYLE = {
  high: "text-emerald-500 border-emerald-500/40",
  medium: "text-amber-500 border-amber-500/40",
  low: "text-red-400 border-red-400/40",
};

export const isReady = (f) => f.qty != null && f.status !== "rejected";
export const needsReview = (f) => f.status !== "rejected" && (f.qty == null || f.confidence === "low");

function EvidenceBlock({ f }) {
  const rows = [
    ["Drawing", `${f.drawing_name || ""} · p${f.page}`],
    ["Label", f.evidence?.label],
    ["Schedule", f.evidence?.schedule],
    ["Geometry", f.evidence?.geometry],
    ["Legend", f.evidence?.legend],
    ["Detail", f.evidence?.detail],
    f.from_structure || f.to_structure ? ["Run", `${f.from_structure || "?"} → ${f.to_structure || "?"}`] : null,
    f.grade ? ["Grade", f.grade] : null,
    f.invert_upstream != null || f.invert_downstream != null
      ? ["Inverts", `${f.invert_upstream ?? "?"} → ${f.invert_downstream ?? "?"}`] : null,
    f.pipe_class ? ["Class", f.pipe_class] : null,
  ].filter((r) => r && r[1]);
  return (
    <div className="mt-1.5 px-2 py-1.5 bg-zinc-100 dark:bg-white/[0.04] border border-zinc-200 dark:border-white/10 space-y-0.5" data-testid={`ai-evidence-${f.fid}`}>
      <p className="text-[9px] font-bold uppercase tracking-widest text-[#FF5F15]">AI Evidence</p>
      {rows.map(([k, v]) => (
        <p key={k} className="text-[10px] text-zinc-600 dark:text-zinc-400"><b className="text-zinc-500 uppercase text-[9px]">{k}:</b> {v}</p>
      ))}
      <p className="text-[10px] text-zinc-500"><b className="uppercase text-[9px]">Confidence:</b> <span className="uppercase font-bold">{f.confidence}</span></p>
    </div>
  );
}

function FindingRow({ f, updateFinding, onLocate, onMeasure }) {
  const [showWhy, setShowWhy] = useState(false);
  const rejected = f.status === "rejected";
  const checked = ["accepted", "edited"].includes(f.status) || f.built;
  return (
    <div className={`px-2.5 py-2 ${rejected ? "opacity-45" : ""}`} data-testid={`ai-finding-${f.fid}`}>
      <div className="flex items-start gap-2">
        <Checkbox checked={checked} disabled={f.built}
          onCheckedChange={(v) => updateFinding(f.fid, { status: v ? "accepted" : "pending" })}
          className="mt-0.5" data-testid={`ai-finding-check-${f.fid}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold truncate max-w-[150px]" title={f.name}>{f.name}</span>
            {f.built && <span className="px-1 text-[8px] font-bold uppercase text-emerald-500 border border-emerald-500/40">In takeoff</span>}
            {f.status === "edited" && !f.built && <span className="px-1 text-[8px] font-bold uppercase text-sky-400 border border-sky-400/40">Edited</span>}
            <span className={`px-1 text-[8px] font-bold uppercase border ${CONF_STYLE[f.confidence]}`}>{f.confidence}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <Input type="number" value={f.qty ?? ""} placeholder="?" disabled={f.built}
              onChange={(e) => updateFinding(f.fid, { qty: e.target.value === "" ? null : Number(e.target.value), status: "edited" })}
              className="h-6.5 rounded-sm text-[11px] w-20 font-mono px-1.5" data-testid={`ai-finding-qty-${f.fid}`} />
            <span className="text-[10px] font-mono text-zinc-500 w-6">{f.unit}</span>
            <span className="text-[9px] text-zinc-500 uppercase font-bold">{f.type}</span>
            {f.size && <span className="text-[9px] text-zinc-500">{f.size}{f.material ? ` ${f.material}` : ""}</span>}
            {(f.locations?.length || 0) > 1 && <span className="text-[9px] font-mono text-zinc-400">×{f.locations.length}</span>}
          </div>
          {f.qty == null && !rejected && (
            <p className="text-[10px] text-amber-500 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3 shrink-0" /> Quantity not readable — type it in or measure it on the plan
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            {f.bbox && (
              <button onClick={() => onLocate(f, false)} className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]" data-testid={`ai-locate-${f.fid}`}>
                <Crosshair className="w-3 h-3" /> Locate
              </button>
            )}
            {(f.locations?.length || 0) > 1 && (
              <button onClick={() => onLocate(f, true)} className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]" data-testid={`ai-viewall-${f.fid}`}>
                <Eye className="w-3 h-3" /> All {f.locations.length}
              </button>
            )}
            <button onClick={() => setShowWhy((s) => !s)} className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]" data-testid={`ai-why-${f.fid}`}>
              <HelpCircle className="w-3 h-3" /> Why?
            </button>
            {!f.built && f.qty == null && (
              <button onClick={() => onMeasure(f)} className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid={`ai-measure-${f.fid}`}>
                <Ruler className="w-3 h-3" /> Measure on plan
              </button>
            )}
            <div className="flex-1" />
            {!f.built && (rejected ? (
              <button onClick={() => updateFinding(f.fid, { status: "pending" })} className="text-zinc-500 hover:text-emerald-500" title="Restore" data-testid={`ai-restore-${f.fid}`}>
                <RotateCcw className="w-3 h-3" />
              </button>
            ) : (
              <button onClick={() => updateFinding(f.fid, { status: "rejected" })} className="text-zinc-500 hover:text-red-500" title="Reject" data-testid={`ai-reject-${f.fid}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            ))}
          </div>
          {showWhy && <EvidenceBlock f={f} />}
        </div>
      </div>
    </div>
  );
}

export default function TakeoffAIPanel({
  open, onClose, findings, issues, updateFinding, updateIssue,
  onLocate, onMeasure, onRescan, onBuild, onQuickAdd,
}) {
  const [tab, setTab] = useState("found");
  const [collapsed, setCollapsed] = useState({});
  const live = findings.filter((f) => f.status !== "rejected");
  const rejected = findings.filter((f) => f.status === "rejected");
  const ready = live.filter(isReady);
  const review = findings.filter(needsReview);
  const openIssues = issues.filter((i) => i.status !== "dismissed");
  const acceptedUnbuilt = findings.filter((f) => ["accepted", "edited"].includes(f.status) && !f.built && f.qty != null);
  const confCount = (c) => live.filter((f) => f.confidence === c).length;

  const groups = useMemo(() => {
    const src = tab === "review" ? review : [...live, ...rejected];
    const by = {};
    src.forEach((f) => { (by[f.discipline || "Other"] = by[f.discipline || "Other"] || []).push(f); });
    return Object.entries(by).sort((a, b) => b[1].length - a[1].length);
  }, [findings, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  return (
    <div className="fixed right-0 top-[84px] bottom-0 w-full sm:w-[400px] z-50 flex flex-col bg-white dark:bg-[#0F141C] border-l border-zinc-200 dark:border-white/10 shadow-2xl" data-testid="ai-panel">
      {/* header */}
      <div className="shrink-0 px-3 py-2.5 border-b border-zinc-200 dark:border-white/10">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-display font-black uppercase tracking-tight text-sm">AI Plan Reader</span>
          <div className="flex-1" />
          <button onClick={onRescan} className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]" data-testid="ai-panel-rescan">Re-scan</button>
          <button onClick={onClose} className="text-zinc-400 hover:text-[#FF5F15]" data-testid="ai-panel-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-3 mt-1.5" data-testid="ai-panel-summary">
          <span className="text-[11px]"><b className="text-[#FF5F15] font-mono">{live.length}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">found</span></span>
          <span className="text-[11px]"><b className="text-emerald-500 font-mono">{ready.length}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">ready</span></span>
          <span className="text-[11px]"><b className="text-amber-500 font-mono">{review.length}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">need review</span></span>
          <div className="flex-1" />
          <span className="text-[9px] font-mono text-zinc-500">H{confCount("high")} · M{confCount("medium")} · L{confCount("low")}</span>
        </div>
      </div>
      {/* tabs */}
      <div className="shrink-0 flex border-b border-zinc-200 dark:border-white/10">
        {[["found", `Found ${live.length}`], ["review", `Review ${review.length}`], ["issues", `Issues ${openIssues.length}`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === k ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500"}`}
            data-testid={`ai-panel-tab-${k}`}>{label}</button>
        ))}
      </div>
      {/* body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === "issues" ? (
          <div className="divide-y divide-zinc-100 dark:divide-white/5">
            {!openIssues.length && <p className="px-3 py-6 text-xs text-zinc-500 text-center">No potential issues flagged on the scanned sheets.</p>}
            {openIssues.map((i) => (
              <div key={i.id} className="px-3 py-2.5" data-testid={`ai-issue-${i.id}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px]">{i.text}</p>
                    <p className="text-[9px] text-zinc-500 uppercase font-bold mt-0.5">{i.kind} · {i.drawing_name} p{i.page}</p>
                  </div>
                  <button onClick={() => updateIssue(i.id, { status: "dismissed" })} className="text-zinc-500 hover:text-red-500 text-[9px] font-bold uppercase" data-testid={`ai-issue-dismiss-${i.id}`}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        ) : groups.length ? groups.map(([disc, fs]) => (
          <div key={disc}>
            <button onClick={() => setCollapsed((c) => ({ ...c, [disc]: !c[disc] }))}
              className="w-full flex items-center gap-2 px-3 py-1.5 bg-zinc-50 dark:bg-white/[0.03] text-[10px] font-bold uppercase tracking-widest text-zinc-500 sticky top-0"
              data-testid={`ai-group-${disc.replace(/[^a-z]/gi, "")}`}>
              {collapsed[disc] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span className="w-2 h-2 rounded-full" style={{ background: DISCIPLINE_COLORS[disc] || "#71717a" }} />
              {disc} <span className="font-mono text-zinc-400">{fs.filter((f) => f.status !== "rejected").length}</span>
            </button>
            {!collapsed[disc] && (
              <div className="divide-y divide-zinc-100 dark:divide-white/5">
                {fs.map((f) => <FindingRow key={f.fid} f={f} updateFinding={updateFinding} onLocate={onLocate} onMeasure={onMeasure} />)}
              </div>
            )}
          </div>
        )) : (
          <p className="px-3 py-6 text-xs text-zinc-500 text-center">{tab === "review" ? "Nothing needs review — nice." : "No findings yet — run a scan."}</p>
        )}
      </div>
      {/* footer */}
      <div className="shrink-0 p-2.5 border-t border-zinc-200 dark:border-white/10 space-y-1.5">
        <button
          onClick={() => live.filter((f) => f.confidence === "high" && f.status === "pending" && f.qty != null)
            .forEach((f) => updateFinding(f.fid, { status: "accepted" }))}
          className="w-full flex items-center justify-center gap-1 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] border border-dashed border-zinc-300 dark:border-white/15"
          data-testid="ai-select-high">
          <CheckCheck className="w-3 h-3" /> Select all high confidence
        </button>
        <div className="flex gap-1.5">
          <Button variant="outline" onClick={onQuickAdd} disabled={!acceptedUnbuilt.length}
            className="h-9 rounded-sm text-[10px] font-bold uppercase tracking-wider flex-1" data-testid="ai-quick-add">
            Add {acceptedUnbuilt.length || ""} selected
          </Button>
          <Button onClick={onBuild} disabled={!acceptedUnbuilt.length}
            className="h-9 rounded-sm flex-1 bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase tracking-wider" data-testid="ai-build-draft">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Review takeoff
          </Button>
        </div>
      </div>
    </div>
  );
}
